// LIVE.POLISH E: a tab running the previous release - one check for every form it
// takes, one way of handling it.
//
// A release replaces the server's code, chunks and server-action ids while an open
// tab keeps the old ones. The old tab then fails in several ways, all one cause:
//   - "Loading chunk 7177 failed" / ChunkLoadError       - a code file the new
//     release no longer has (12, 13, 21 Oct);
//   - "Failed to find Server Action ..."                 - an action id it lacks;
//   - "Cannot read properties of undefined (reading 'call')" thrown inside webpack's
//     loader - the old page asked for a module the new chunk does not carry
//     (/login, 29 Oct, a release day);
//   - "An unexpected response was received from the server." (14 Oct), "Invalid
//     Server Actions request." and "An error occurred in the Server Components
//     render." (21 Oct 10:00, the release) - the new server answering an old page.
// The first three can only be skew. The last three can also be a real server fault,
// so they count as skew only when the deployed build differs from the tab's
// (GET /api/build-id against BUILD_ID): a real fault on the current release is
// still logged as the error it is (rule 30).
//
// Before this, isChunkLoadError (reportClientError) and isVersionSkewError
// (app/error.tsx) each knew part of the list and global-error.tsx knew none of it.
// Every handler now asks this module.

import { BUILD_ID } from "@/lib/build-id";

export type SkewSignal = "definite" | "possible" | null;

const DEFINITE: RegExp[] = [
  /loading (css )?chunk [\w-]+ failed/i,
  /chunkloaderror/i,
  /importing a module script failed/i,
  /failed to fetch dynamically imported module/i,
  /failed to find server action/i,
  /server action .{0,80}not found/i,
];

const POSSIBLE: RegExp[] = [
  /an unexpected response was received from the server/i,
  /invalid server actions request/i,
  /an error occurred in the server components render/i,
];

function parts(reason: unknown): { name: string; message: string; stack: string } {
  if (reason instanceof Error) return { name: reason.name ?? "", message: reason.message ?? "", stack: reason.stack ?? "" };
  if (typeof reason === "string") return { name: "", message: reason, stack: "" };
  const r = reason as { name?: unknown; message?: unknown; stack?: unknown } | null;
  return { name: String(r?.name ?? ""), message: String(r?.message ?? ""), stack: String(r?.stack ?? "") };
}

/**
 * How surely a failure is a stale-release failure: "definite" for the forms only a
 * previous release produces, "possible" for those a real server fault can also
 * produce (confirm with deployedBuildDiffers), null otherwise. Pure, for the smoke.
 */
export function skewSignal(reason: unknown): SkewSignal {
  const { name, message, stack } = parts(reason);
  if (name === "ChunkLoadError") return "definite";
  if (DEFINITE.some((re) => re.test(message))) return "definite";
  // webpack's loader calls modules[id].call(...); an id the loaded chunks do not
  // hold reads `call` of undefined, inside the webpack runtime chunk. The same
  // message from application code is not this, so the frame is required.
  if (/cannot read properties of undefined \(reading 'call'\)|undefined is not an object \(evaluating '[^']*\.call'\)/i.test(message) && /\/_next\/static\/chunks\/webpack-/.test(stack)) {
    return "definite";
  }
  if (POSSIBLE.some((re) => re.test(message))) return "possible";
  return null;
}

/** True when the deployed build differs from this tab's. False when it matches,
 *  when the build id is unknown ("dev") or when the check cannot be made. */
export async function deployedBuildDiffers(fetchImpl: typeof fetch = fetch, tabBuild: string = BUILD_ID): Promise<boolean> {
  if (!tabBuild || tabBuild === "dev") return false;
  try {
    const res = await fetchImpl("/api/build-id", { cache: "no-store" });
    if (!res.ok) return false;
    const data = (await res.json()) as { buildId?: string };
    return Boolean(data.buildId) && data.buildId !== tabBuild;
  } catch {
    return false;
  }
}

/** The one release-skew check: definite forms at once, possible forms only when
 *  the deployed build differs from the tab's. */
export async function isReleaseSkew(reason: unknown, fetchImpl: typeof fetch = fetch, tabBuild: string = BUILD_ID): Promise<boolean> {
  const s = skewSignal(reason);
  if (s === "definite") return true;
  if (s === "possible") return deployedBuildDiffers(fetchImpl, tabBuild);
  return false;
}

// ---- handling: one guarded reload, or one plain prompt -----------------------

export const RELEASE_PROMPT = "Trayvar has been updated. Reload to continue.";
const RELOAD_KEY = "trayvar:releaseReload";

/**
 * Whether to reload now: at most once per tab session (a release that is itself
 * broken reloads once, then prompts rather than looping), and never over typed
 * input when the page is still alive. Pure, for the smoke.
 */
export function planSkewRecovery(opts: { alreadyReloaded: boolean; hasTypedInput: boolean; pageReplaced: boolean }): "reload" | "prompt" {
  if (opts.alreadyReloaded) return "prompt";
  if (opts.hasTypedInput && !opts.pageReplaced) return "prompt";
  return "reload";
}

type PromptListener = () => void;
const promptListeners = new Set<PromptListener>();
let promptPending = false;

/** Show the one release prompt (ReleasePrompt in the root layout renders it). */
export function showReleasePrompt(): void {
  promptPending = true;
  for (const l of promptListeners) l();
}

/** Subscribe the mounted prompt; a prompt raised before it mounted is delivered at once. */
export function subscribeReleasePrompt(listener: PromptListener): () => void {
  promptListeners.add(listener);
  if (promptPending) listener();
  return () => {
    promptListeners.delete(listener);
  };
}

// Inputs the operator has typed into this session, so a reload never silently
// discards them. Registered once by registerGlobalErrorListeners.
const typed = new Set<EventTarget>();
export function trackTypedInput(): void {
  if (typeof document === "undefined") return;
  document.addEventListener(
    "input",
    (e) => {
      if (e.target) typed.add(e.target);
    },
    true,
  );
}

/** True while an input the operator typed into is still on the page with a value. */
export function hasTypedInput(): boolean {
  for (const t of typed) {
    const el = t as Partial<HTMLInputElement> & { isConnected?: boolean };
    if (!el.isConnected) {
      typed.delete(t);
      continue;
    }
    if (typeof el.value === "string" && el.value.length > 0 && el.type !== "password" && el.type !== "hidden") return true;
  }
  return false;
}

/**
 * Recover from a recognised stale release: one guarded reload, or the prompt where
 * a reload could lose typed input (or the one reload has been spent). Returns what
 * it did. `pageReplaced` is true from an error boundary, where the page is already
 * gone and a reload loses nothing more. Never throws.
 */
export function recoverFromSkew(pageReplaced: boolean): "reload" | "prompt" {
  let already = false;
  try {
    already = sessionStorage.getItem(RELOAD_KEY) === "1";
  } catch {
    already = true; // no storage, no guard: prompt rather than risk a loop
  }
  const plan = planSkewRecovery({ alreadyReloaded: already, hasTypedInput: hasTypedInput(), pageReplaced });
  try {
    if (plan === "reload") {
      sessionStorage.setItem(RELOAD_KEY, "1");
      window.location.reload();
    } else {
      showReleasePrompt();
    }
  } catch {
    showReleasePrompt();
  }
  return plan;
}

/** Clear the reload guard once a page has stayed up, so a later release can reload
 *  once again. A reload loop cannot clear it in the window. */
export function clearSkewGuardLater(ms = 5000): void {
  try {
    window.setTimeout(() => {
      try {
        sessionStorage.removeItem(RELOAD_KEY);
      } catch {
        // Storage blocked: nothing to clear.
      }
    }, ms);
  } catch {
    // Never throw during startup.
  }
}
